const TRAILING_MEASURED_DETAIL_PATTERN = /\s*\([^()]*[:：][^()]*\)\s*$/;
const COMMAND_DELIVERY_COMPLETED_PATTERN = /^(.+?)\s*명령\s+전달\s+완료(?:\s*\(([^()]*)\))?\s*$/;
const COMMAND_DELIVERY_FAILURE_TITLE = '장비 명령을 전송하지 못했습니다.';
const COMMAND_DELIVERY_A1_GPS_DESCRIPTION = '위험알리미 A가 위치 정보를 확인하는 중입니다. 잠시 후 다시 실행해 주세요.';
const COMMAND_DELIVERY_A1_GPS_REASON_MARKERS = ['위험알리미 A GPS 시작 상태', 'GPS 연결로 인해 활성 하향 route가 차단되었습니다.'];

export interface CommandDeliveryDisplay {
  title: string;
  description: string;
}

export interface EntryAlarmDisplay {
  title: string;
  content: string;
}

/** 저장된 이름 후보만 사용하며 빈 값·미등록 표시는 대상 이름으로 추정하지 않습니다. */
function readEntryAlarmText(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '-' ? value.trim() : '';
}

/** V2 이동의 생성 당시 이름·대상 구분을 보존하며 현재 지급 관계로 이력을 바꾸지 않습니다. */
export function resolveEntryAlarmDisplay(input: {
  provider?: unknown;
  event?: unknown;
  title?: unknown;
  content?: unknown;
  summaryContent?: unknown;
  deviceName?: unknown;
  archiveReadOnly?: unknown;
}): EntryAlarmDisplay | null {
  if (input.provider !== 'V2' || input.archiveReadOnly === true) return null;
  // 진출·OFF는 서버가 확정한 이름과 사유를 그대로 사용해 현재 장비명으로 덧쓰지 않습니다.
  if (input.event === 'MOVEMENT_EXIT') {
    const title = readEntryAlarmText(input.title);
    const content = readEntryAlarmText(input.content);
    return title && content ? { title, content } : null;
  }
  if (input.event !== 'MOVEMENT_ENTRY') return null;

  const matched = readEntryAlarmText(input.content).match(/^(.*?)\s*(근로자|장비)가\s+현장에\s+진입했습니다\.?$/);
  const summary = readEntryAlarmText(input.summaryContent);
  const title = readEntryAlarmText(input.title);
  const savedName = matched
    ? readEntryAlarmText(matched[1]) ||
      (summary.endsWith('-진입') ? readEntryAlarmText(summary.slice(0, -3)) : '') ||
      (title.endsWith(' 진입 알림') ? readEntryAlarmText(title.slice(0, -6)) : '')
    : '';
  // 대상 종류를 복원할 수 없는 문구는 수령인 이름을 추정하지 않고 장비로 표시합니다.
  const name = savedName || readEntryAlarmText(input.deviceName) || '장비';
  const subject = savedName && matched ? matched[2] : '장비';
  return { title: `${name} 진입 알림`, content: `${name} ${subject}가 현장에 진입했습니다` };
}

/** 장비명이 이미 표시 문자열의 접두어인지 구분자까지 확인합니다. */
export function hasAlarmDeviceNamePrefix(value: string, deviceName: string): boolean {
  const normalizedValue = value.trim();
  const normalizedDeviceName = deviceName.trim();
  if (!normalizedDeviceName || !normalizedValue.startsWith(normalizedDeviceName)) {
    return false;
  }

  const suffix = normalizedValue.slice(normalizedDeviceName.length);
  return suffix.length === 0 || /^(?:\s*-\s*|\s+)/.test(suffix);
}

/** 명령 전달 결과를 사용자용 제목과 안내 문구로 변환합니다. */
export function resolveCommandDeliveryDisplay(value: string | null | undefined, deviceName?: string | null): CommandDeliveryDisplay | null {
  const source = value?.trim();
  if (!source) {
    return null;
  }

  if (COMMAND_DELIVERY_A1_GPS_REASON_MARKERS.some(marker => source.includes(marker))) {
    return {
      title: COMMAND_DELIVERY_FAILURE_TITLE,
      description: COMMAND_DELIVERY_A1_GPS_DESCRIPTION,
    };
  }

  const matched = source.match(COMMAND_DELIVERY_COMPLETED_PATTERN);
  if (!matched) {
    return null;
  }

  const sourceName = matched[1]?.trim();
  const normalizedDeviceName = deviceName?.trim();
  const displayName = normalizedDeviceName || sourceName;
  if (!displayName) {
    return null;
  }

  const title = `${displayName} 명령 전달 완료`;
  const commandDetail = matched[2]?.trim();
  return {
    title,
    description: commandDetail || title,
  };
}

/** 일반 계측 괄호는 제거하되 명령 ACK의 실제 전달값은 보존합니다. */
export function removeAlarmMeasuredDetailText(value: string | null | undefined): string {
  const source = value?.trim() ?? '';
  if (resolveCommandDeliveryDisplay(source)) {
    return source;
  }
  return source.replace(TRAILING_MEASURED_DETAIL_PATTERN, '').trim();
}

/** 지도 팝업의 경고 사유에서 대상 장비명과 계측 상세를 제거합니다. */
export function summarizeAlarmWarningReason(value: string | null | undefined, displayDeviceName?: string | null): string {
  let summary = removeAlarmMeasuredDetailText(value)
    .replace(/\s*-\s*/g, '-')
    .trim();
  // 비교할 이름도 사유와 같은 규칙으로 정리해 하이픈 공백 차이로 장비명이 중복되지 않게 합니다.
  const normalizedDisplayDeviceName = displayDeviceName?.replace(/\s*-\s*/g, '-').trim();

  if (normalizedDisplayDeviceName && summary.startsWith(normalizedDisplayDeviceName)) {
    const suffix = summary.slice(normalizedDisplayDeviceName.length);
    if (/^(?:\s|[-:：])/.test(suffix)) {
      summary = suffix.replace(/^\s*(?:[-:：])?\s*/, '');
    }
  }

  if (normalizedDisplayDeviceName) {
    const connectors = [`-${normalizedDisplayDeviceName}`, `의 ${normalizedDisplayDeviceName}`, `의${normalizedDisplayDeviceName}`];

    for (const connector of connectors) {
      const connectorIndex = summary.indexOf(connector);
      if (connectorIndex <= 0) {
        continue;
      }

      const suffix = summary.slice(connectorIndex + connector.length);
      if (suffix && !/^(?:\s|[-:：])/.test(suffix)) {
        continue;
      }

      summary = `${summary.slice(0, connectorIndex)}-${suffix.replace(/^\s*(?:[-:：])?\s*/, '')}`;
      break;
    }

    // 위치 문구 뒤의 장비명도 제거하되 다른 이름의 일부는 보존하고 특수문자는 문자 그대로 비교합니다.
    const escapedDeviceName = normalizedDisplayDeviceName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    summary = summary.replace(new RegExp(`\\s+${escapedDeviceName}(?=\\s|[-:：]|$)`, 'g'), ' ');
  }

  return summary
    .replace(/\s*-\s*/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-:：\s]+|[-:：\s]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
